import React from 'react';
import { Download, FileText, Lock } from 'lucide-react';
import { Transaction } from '../../types';
import { FYCgtResult } from '../../utils/cgtCalculator';
import {
  buildTransactionsCsv,
  downloadCsv,
  transactionsFilename,
} from '../../utils/csvExport';
import { downloadCgtReportPdf } from '../../utils/pdfExport';
import { useUpgradePrompt } from '../../context/UpgradePromptContext';
import { FEATURE_GATES } from '../../config/limits';

/**
 * Export toolbar for the Realised CGT sub-tab. Builds the reports from the data
 * TaxView already computed (no recompute) and triggers a browser download. The
 * figures arrive in AUD, already rounded by the engine, so nothing here converts
 * or re-rounds — that is what keeps the PDF identical to the screen.
 * Slate + bronze, theme-aware via the app's slate tokens.
 *
 * When `locked` (Free users), the buttons show a lock and clicking opens the
 * <UpgradePrompt> instead of downloading — the export logic itself is unchanged.
 */
interface Props {
  result: FYCgtResult;
  fy: { startYear: number; endYear: number };
  marginalRate: number;
  transactions: Transaction[];
  locked?: boolean;
}

export function TaxExportButtons({ result, fy, marginalRate, transactions, locked = false }: Props) {
  const { open: openUpgradePrompt } = useUpgradePrompt();

  const handleTransactions = () => {
    if (locked) { openUpgradePrompt(FEATURE_GATES.taxExports); return; }
    const csv = buildTransactionsCsv({ transactions, exportDate: new Date(), fy });
    downloadCsv(transactionsFilename(fy), csv);
  };

  const handleCgtPdf = () => {
    if (locked) { openUpgradePrompt(FEATURE_GATES.taxExports); return; }
    downloadCgtReportPdf({ result, fy, marginalRate, exportDate: new Date() });
  };

  // Small lock badge shown on each button for Free users.
  const lockIcon = locked ? <Lock className="w-3 h-3 opacity-70" /> : null;

  return (
    <div className="flex flex-wrap items-center gap-2.5 mt-5">
      {/* Headline export — bronze PDF report */}
      <button
        type="button"
        onClick={handleCgtPdf}
        title={locked ? 'Pro feature' : undefined}
        className="flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg transition-colors bg-[rgba(212,180,131,0.1)] hover:bg-[rgba(212,180,131,0.18)] border border-[rgba(212,180,131,0.32)] text-[#b89968]"
      >
        {locked ? lockIcon : <FileText className="w-3.5 h-3.5" />}
        Download CGT Report (PDF)
      </button>

      {/* Raw source data — subtle slate (matches the header's Refresh button) */}
      <button
        type="button"
        onClick={handleTransactions}
        title={locked ? 'Pro feature' : undefined}
        className="flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg transition-colors bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"
      >
        {locked ? lockIcon : <Download className="w-3.5 h-3.5" />}
        Download Transactions (CSV)
      </button>
    </div>
  );
}
